import React from 'react';
import { MessageSquareText, RotateCcw, ExternalLink, Phone, Zap } from 'lucide-react';
import { WhatsAppMessage } from '@/services/integrations/whatsappInboxService';
import { formatMessageTime } from '@/utils/whatsappInbox';
import StatusTicks from './StatusTicks';
import MediaAttachment from './MediaAttachment';

const MEDIA_TYPES = new Set(['image', 'video', 'audio', 'voice', 'document', 'sticker']);

/**
 * An inbound attachment with no caption is stored with a placeholder body —
 * "[audio]", "[image]" — so the conversation list has something to preview.
 * In the thread the attachment itself is right there, so the placeholder is
 * just the word "[audio]" printed under a voice note. A real caption is kept.
 */
const isPlaceholderCaption = (body: string | null, msgType: string | null): boolean =>
  !!body && !!msgType && body.trim() === `[${msgType}]`;

interface MessageBubbleProps {
  message: WhatsAppMessage;
  onRetry?: (message: WhatsAppMessage) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message: m, onRetry }) => {
  const outbound = m.direction === 'outbound';
  const failed = m.status === 'failed';
  const isTemplate = m.msg_type === 'template';
  const isMedia = MEDIA_TYPES.has(m.msg_type || '');

  return (
    <div className={`flex flex-col ${outbound ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[520px] rounded-xl overflow-hidden ${
          outbound
            ? failed
              ? 'bg-white border border-red-200 shadow-[0_0_0_3px_rgba(254,226,226,0.2)] rounded-tr-sm'
              : 'bg-[#eef5f0] border border-[#d9e8e0] rounded-tr-sm'
            : 'bg-white border border-gray-200 rounded-tl-sm'
        }`}
      >
        {isTemplate && m.template_header_image_url && (
          <img src={m.template_header_image_url} alt="" className="w-full max-h-64 object-cover" />
        )}

        <div className="px-3.5 pt-2.5 pb-1.5">
          {isTemplate && (
            <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-dashed border-[#c9ddd2]">
              <MessageSquareText className="w-3 h-3 text-purple-600" />
              <span className="text-[10.5px] font-extrabold text-purple-700 tracking-wide">TEMPLATE</span>
            </div>
          )}

          {isMedia ? (
            <div className="mb-1">
              <MediaAttachment message={m} />
              {m.body && !isPlaceholderCaption(m.body, m.msg_type) && (
                <div className="text-[13px] text-gray-700 mt-1.5 whitespace-pre-wrap">{m.body}</div>
              )}
            </div>
          ) : (
            m.body && <div className="text-[13.5px] leading-relaxed whitespace-pre-wrap break-words">{m.body}</div>
          )}

          {isTemplate && m.template_buttons.length > 0 && (
            <div className="mt-2 -mx-3.5 px-3.5 pt-2 border-t border-dashed border-[#c9ddd2] space-y-1">
              {m.template_buttons.map((b, i) => {
                const isUrl = b.type === 'URL' && b.url;
                const content = (
                  <>
                    {b.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                    {b.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                    {b.text || b.type}
                  </>
                );
                return isUrl ? (
                  <a
                    key={i} href={b.url} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white border border-gray-200 text-[12.5px] font-medium text-[#00a5f4] hover:bg-gray-50"
                  >
                    {content}
                  </a>
                ) : (
                  <div
                    key={i}
                    className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-white border border-gray-200 text-[12.5px] font-medium text-[#00a5f4]"
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          )}

        <div className={`flex items-center gap-1.5 mt-1 ${outbound ? 'justify-end' : 'justify-end'}`}>
          {failed && <span className="text-[11px] font-bold text-red-600">Not delivered ·</span>}
          <span className="text-[10.5px] text-gray-400">{formatMessageTime(m.created_at)}</span>
          {outbound && <StatusTicks status={m.status} />}
        </div>

        {failed && (
          <div className="mt-2 pt-2 border-t border-red-100">
            {(m.error_title || m.error_code) && (
              <div className="text-[12px] font-bold text-red-800 mb-1">
                {m.error_code ? `Meta error ${m.error_code} — ` : ''}{m.error_title || 'message undeliverable'}
              </div>
            )}
            {onRetry && (
              <button
                onClick={() => onRetry(m)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#153d23] text-white text-[11.5px] font-bold hover:opacity-90"
              >
                <RotateCcw className="w-3 h-3" /> Retry send
              </button>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Who sent it. A shared inbox means an outbound bubble could be any of ~60
          agents, a journey, or the payment-link flow — the thread rendered all of
          them identically. Nothing is drawn when the sender is genuinely unknown
          (pre-Inbox rows), since a placeholder there would be a guess. */}
      {m.sent_by_name && (
        <div
          className={`flex items-center gap-1 mt-0.5 px-1 text-[10.5px] leading-none ${
            m.sent_by_kind === 'automation' ? 'text-purple-600' : 'text-gray-400'
          }`}
        >
          {m.sent_by_kind === 'automation' && <Zap className="w-2.5 h-2.5 flex-shrink-0" />}
          <span className="truncate max-w-[320px]">
            {m.sent_by_kind === 'customer' ? m.sent_by_name : `Sent by ${m.sent_by_name}`}
          </span>
        </div>
      )}
    </div>
  );
};

export default MessageBubble;
